import { useState } from 'react';
import { Box, Dialog } from '@mui/material';
import { useSelector } from 'react-redux';
import { MainAppReducer } from 'Types';
import { PLAY_INPUT_DIALOG_Z_INDEX } from 'Constants/StackingLevels';
import { useHardwareBackClosesLayer } from 'Hooks/useHardwareBack';
import {
  PLAY_INPUT_OPTIONS,
  PLAY_INPUT_QUESTION,
  accountStoresPlayInput,
  shownPlayInput,
} from 'Utils/playAlong/playInputAccount';
import PlayInputPicker from './PlayInputPicker';

export interface PlayInputDialogProps {
  open: boolean;
  onClose: () => void;
}

/**
 * The input picker in a dialog, for a play surface's menu: the same control as
 * Settings > Practice, on the one account value. Closes once a new answer is
 * saved.
 */
export function PlayInputDialog({ open, onClose }: PlayInputDialogProps) {
  // Android's Back closes the picker as a tap outside it does.
  useHardwareBackClosesLayer(open, onClose, PLAY_INPUT_DIALOG_Z_INDEX);
  return (
    <Dialog
      open={open}
      onClose={onClose}
      sx={{ zIndex: PLAY_INPUT_DIALOG_Z_INDEX }}
      slotProps={{
        paper: {
          sx: {
            backgroundColor: '#050B13',
            border: '1px solid #525969',
            borderRadius: '12px',
            padding: '20px 24px',
            maxWidth: '520px',
          },
        },
      }}
    >
      <PlayInputPicker onSaved={onClose} />
    </Dialog>
  );
}

/**
 * The "What do you play on?" row in a play surface's settings or options panel.
 * It shows the current answer and opens the picker. Hidden where the backend
 * does not store the answer yet.
 */
export default function PlayInputMenuEntry() {
  const userData = useSelector((state: MainAppReducer) => state.mainAppReducer?.userData);
  const [open, setOpen] = useState(false);

  if (!accountStoresPlayInput(userData)) return null;
  const current = PLAY_INPUT_OPTIONS.find(
    (option) => option.value === shownPlayInput(userData?.play_input),
  );

  return (
    <>
      <Box
        component="button"
        type="button"
        data-testid="play-input-menu-entry"
        aria-haspopup="dialog"
        onClick={() => setOpen(true)}
        sx={{
          width: '100%',
          minHeight: '44px',
          margin: '8px 0',
          padding: '8px 0',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: '2px',
          border: 'none',
          background: 'transparent',
          color: '#ECECED',
          fontFamily: 'Lato',
          fontSize: '16px',
          textAlign: 'left',
          cursor: 'pointer',
        }}
      >
        <span>{PLAY_INPUT_QUESTION}</span>
        <Box component="span" sx={{ fontSize: '13px', color: '#9CA3AF' }}>
          {current?.title}
        </Box>
      </Box>
      <PlayInputDialog open={open} onClose={() => setOpen(false)} />
    </>
  );
}
